import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatINR, fmtDate, fmtDateTime } from "@/lib/roles";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { FileCheck, Loader2, RefreshCw, Wallet, ClipboardCheck, ShieldCheck } from "lucide-react";

const STATUS_LABELS = {
  submitted: "Submitted",
  under_review: "Under Review",
  inspection_scheduled: "Inspection Scheduled",
  inspection_completed: "Inspection Completed",
  approved: "Approved",
  rejected: "Rejected",
  renewed: "Renewed",
  expired: "Expired",
};

export default function LicenseDetail() {
  const { lid } = useParams();
  const { user } = useAuth();
  const isCitizen = user?.role === "citizen";
  const canReview = user && ["executive_officer", "revenue_officer", "tax_inspector", "municipal_commissioner", "state_admin", "super_admin"].includes(user.role);
  const canInspect = user && ["tax_inspector", "executive_officer", "revenue_officer", "municipal_commissioner", "state_admin", "super_admin"].includes(user.role);
  const [data, setData] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [remarks, setRemarks] = useState("");
  const [outcome, setOutcome] = useState("");
  const [insRemarks, setInsRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/licenses/${lid}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [lid]);

  const changeStatus = async () => {
    if (!newStatus) return toast.error("Pick a status");
    setBusy(true);
    try {
      await api.post(`/licenses/${data.license.id}/status`, { status: newStatus, remarks });
      toast.success(`Status updated to ${STATUS_LABELS[newStatus] || newStatus}`);
      setNewStatus(""); setRemarks(""); load();
    } catch (err) { toast.error("Update failed", { description: err.response?.data?.detail }); }
    finally { setBusy(false); }
  };

  const addInspection = async () => {
    setBusy(true);
    try {
      await api.post(`/licenses/${data.license.id}/inspection`, { outcome, remarks: insRemarks, scheduled_date: new Date().toISOString() });
      toast.success("Inspection recorded");
      setOutcome(""); setInsRemarks(""); load();
    } catch (err) { toast.error("Inspection failed", { description: err.response?.data?.detail }); }
    finally { setBusy(false); }
  };

  const renew = async () => {
    setBusy(true);
    try {
      const r = await api.post("/licenses/renew", { license_id: data.license.id });
      toast.success(`Renewal demand created: ${formatINR(r.data.fee)}`);
      load();
    } catch (err) { toast.error("Renewal failed", { description: err.response?.data?.detail }); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const a = data.license;
  const demands = data.demands || [];

  return (
    <div className="space-y-6" data-testid="license-detail-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Trade License</div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold flex items-center gap-2">
            <FileCheck className="w-6 h-6 text-[#0D7650]" /> {a.business_name}
          </h1>
          <div className="text-xs font-mono text-slate-500 mt-1">{a.license_code}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge className="capitalize text-sm h-8 px-3" variant={a.status === "approved" || a.status === "renewed" ? "default" : a.status === "rejected" || a.status === "expired" ? "destructive" : "outline"}>
            {STATUS_LABELS[a.status] || a.status}
          </Badge>
          {["approved", "renewed", "expired"].includes(a.status) && (
            <Button onClick={renew} disabled={busy} className="bg-[#0D7650] hover:bg-[#095a3d] text-white" data-testid="btn-renew-license">
              <RefreshCw className="w-4 h-4 mr-1" /> Renew
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2"><CardContent className="p-6 grid grid-cols-2 gap-4 text-sm">
          <div><div className="text-xs text-slate-500">Owner</div><div>{a.owner_name}</div></div>
          <div><div className="text-xs text-slate-500">Contact</div><div>{a.owner_phone || a.owner_email || "—"}</div></div>
          <div className="col-span-2"><div className="text-xs text-slate-500">Address</div><div>{a.address}</div></div>
          <div><div className="text-xs text-slate-500">Business Type</div><div className="capitalize">{(a.business_type || "").replace(/_/g, " ")}</div></div>
          <div><div className="text-xs text-slate-500">Ward / Zone</div><div>{a.ward} · {a.zone || "—"}</div></div>
          <div><div className="text-xs text-slate-500">Area</div><div>{a.area_sqft} sqft</div></div>
          <div><div className="text-xs text-slate-500">Employees</div><div>{a.number_of_employees}</div></div>
          <div><div className="text-xs text-slate-500">Capital Investment</div><div>{formatINR(a.capital_investment)}</div></div>
          <div><div className="text-xs text-slate-500">Issue / Expiry</div><div>{fmtDate(a.issue_date)} → {fmtDate(a.expiry_date)}</div></div>
          {a.certificate_number && <div className="col-span-2"><div className="text-xs text-slate-500">Certificate #</div><div className="font-mono">{a.certificate_number}</div></div>}
          {a.rejection_reason && <div className="col-span-2 p-3 rounded bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900"><div className="text-xs text-red-600 font-semibold">Rejection Reason</div><div className="text-sm text-red-700 dark:text-red-300 mt-1">{a.rejection_reason}</div></div>}
        </CardContent></Card>

        <Card className="bg-[#0F2942] text-white"><CardContent className="p-6">
          <div className="text-xs uppercase tracking-widest opacity-70">Annual Fee</div>
          <div className="text-3xl font-heading font-bold mt-2">{formatINR(a.fee_amount)}</div>
          {a.certificate_number && <div className="mt-4 flex items-center gap-2 text-xs opacity-90"><ShieldCheck className="w-4 h-4" /> Certified</div>}
        </CardContent></Card>
      </div>

      {/* Officer actions */}
      {!isCitizen && canReview && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card><CardContent className="p-6 space-y-3">
            <div className="text-sm font-semibold flex items-center gap-2"><ClipboardCheck className="w-4 h-4" /> Change Status</div>
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger data-testid="select-new-status"><SelectValue placeholder="Select next status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="inspection_scheduled">Inspection Scheduled</SelectItem>
                <SelectItem value="approved">Approve</SelectItem>
                <SelectItem value="rejected">Reject</SelectItem>
              </SelectContent>
            </Select>
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Remarks (optional)" data-testid="input-status-remarks" />
            <Button onClick={changeStatus} disabled={busy || !newStatus} className="w-full bg-[#0F2942] hover:bg-[#1E3A5F] text-white" data-testid="btn-update-status">Update Status</Button>
          </CardContent></Card>

          {canInspect && (
            <Card><CardContent className="p-6 space-y-3">
              <div className="text-sm font-semibold flex items-center gap-2"><ClipboardCheck className="w-4 h-4" /> Record Inspection</div>
              <Select value={outcome} onValueChange={setOutcome}>
                <SelectTrigger data-testid="select-inspection-outcome"><SelectValue placeholder="Outcome" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="satisfactory">Satisfactory</SelectItem>
                  <SelectItem value="issues_found">Issues Found</SelectItem>
                </SelectContent>
              </Select>
              <Textarea rows={2} value={insRemarks} onChange={(e) => setInsRemarks(e.target.value)} placeholder="Inspection notes" data-testid="input-inspection-remarks" />
              <Button onClick={addInspection} disabled={busy || !outcome} className="w-full bg-[#0D7650] hover:bg-[#095a3d] text-white" data-testid="btn-add-inspection">Record</Button>
            </CardContent></Card>
          )}
        </div>
      )}

      {/* Timeline */}
      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b border-slate-200 dark:border-slate-800 text-sm font-semibold">Workflow Timeline</div>
          <div className="p-4 space-y-3" data-testid="license-timeline">
            {(a.workflow_log || []).map((l) => (
              <div key={l.id} className="flex gap-3 items-start">
                <div className="w-2 h-2 mt-2 rounded-full bg-[#0F2942] dark:bg-white" />
                <div className="flex-1 border-b border-slate-200 dark:border-slate-800 pb-3 last:border-0">
                  <div className="text-xs text-slate-500">{fmtDateTime(l.at)} · <span className="capitalize">{(l.actor_role || "").replace(/_/g, " ")}</span></div>
                  <div className="text-sm">
                    {l.from_status ? <><span className="capitalize">{l.from_status.replace(/_/g, " ")}</span> → </> : null}
                    <span className="font-semibold capitalize">{(l.to_status || "").replace(/_/g, " ")}</span>
                  </div>
                  {l.remarks && <div className="text-xs text-slate-500 mt-1">{l.remarks}</div>}
                </div>
              </div>
            ))}
            {(a.workflow_log || []).length === 0 && <div className="text-sm text-slate-500 text-center py-4">No activity yet.</div>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b border-slate-200 dark:border-slate-800 text-sm font-semibold">Payment Demands</div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left py-2 px-4">Demand ID</th>
                <th className="text-left py-2 px-4">Period</th>
                <th className="text-right py-2 px-4">Amount</th>
                <th className="text-left py-2 px-4">Status</th>
                <th className="text-right py-2 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {demands.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-slate-500">No demands.</td></tr>}
              {demands.map((d) => (
                <tr key={d.id} className="border-t border-slate-200 dark:border-slate-800">
                  <td className="py-2 px-4 font-mono text-xs">{d.demand_id}</td>
                  <td className="py-2 px-4">{d.period}</td>
                  <td className="py-2 px-4 text-right font-mono">{formatINR(d.total)}</td>
                  <td className="py-2 px-4"><Badge variant={d.status === "paid" ? "default" : "outline"} className="capitalize">{d.status}</Badge></td>
                  <td className="py-2 px-4 text-right">
                    {d.status !== "paid" && <Link to={isCitizen ? `/portal/pay/${d.id}` : `/payments?demand=${d.id}`}><Button size="sm" variant="outline" className="gap-1"><Wallet className="w-3.5 h-3.5" /> Pay</Button></Link>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
